import { useState } from 'react';
import { CheckCircle2, Loader2, XCircle, RefreshCw } from 'lucide-react';
import { useSyncStatus } from '../../hooks/useSyncStatus';
import { Button, buttonVariants, cn } from '@examflow/ui';

function formatLastSync(timestamp: number | null): string {
  if (!timestamp) return '从未同步';
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);

  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  if (hours < 24) return `${hours} 小时前`;
  return new Date(timestamp).toLocaleDateString('zh-CN');
}

export function SyncStatusIndicator() {
  const { status, lastSyncTime, triggerSync, isSyncing } = useSyncStatus();
  const [showTooltip, setShowTooltip] = useState(false);

  const iconClass = cn('h-4 w-4 transition-colors', isSyncing && 'animate-spin');

  const Icon = status === 'synced' ? CheckCircle2 : status === 'syncing' ? Loader2 : XCircle;

  const iconColor =
    status === 'synced'
      ? 'text-state-success'
      : status === 'syncing'
        ? 'text-state-warning'
        : 'text-state-error';

  return (
    <div className="relative inline-flex items-center gap-2">
      <button
        type="button"
        onClick={triggerSync}
        disabled={isSyncing}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 rounded-sm px-2 text-ink-secondary hover:text-ink-primary disabled:cursor-not-allowed'
        )}
        aria-label={`Sync status: ${status}. Last synced: ${formatLastSync(lastSyncTime)}`}
      >
        <span className={iconColor}>
          <Icon className={iconClass} />
        </span>
        {isSyncing && <span className="text-xs font-semibold">同步中...</span>}
      </button>

      {showTooltip && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-40 rounded-sm border border-sage-100 bg-bg-surface p-3 shadow-sm">
          <p className="text-xs font-bold text-ink-muted">同步状态</p>
          <p className="mt-1 text-sm font-semibold text-ink-primary">
            {status === 'synced' ? '已同步' : status === 'syncing' ? '同步中...' : '同步失败'}
          </p>
          <p className="mt-1 text-xs text-ink-secondary">
            上次同步：{formatLastSync(lastSyncTime)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 w-full rounded-sm"
            onClick={(e) => {
              e.stopPropagation();
              triggerSync();
            }}
            disabled={isSyncing}
          >
            <RefreshCw className={cn('h-3 w-3', isSyncing && 'animate-spin')} />
            立即同步
          </Button>
        </div>
      )}
    </div>
  );
}
